import { useState, useEffect } from 'react'
import { deviceService, companyService, dispositivoService } from '../services/api'
import { useSocket } from '../contexts/SocketContext'
import { 
  Plus, Search, Filter, Edit, Trash2, Eye, 
  Shield, ShieldOff, RefreshCw, Monitor, MapPin, Clock, Wifi 
} from 'lucide-react'
import toast from 'react-hot-toast'

const Devices = () => {
  const [devices, setDevices] = useState([])
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [companyFilter, setCompanyFilter] = useState('')
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total: 0,
    per_page: 10
  })
  const [showModal, setShowModal] = useState(false)
  const [editingDevice, setEditingDevice] = useState(null)
  const [loadingCommands, setLoadingCommands] = useState({}) // Para tracking de comandos en progreso
  const [formData, setFormData] = useState({
    device_id: '',
    name: '',
    model: '',
    brand: '',
    os_version: '',
    serial_number: '',
    imei: '',
    status: 'no_registrado',
    location: '',
    company_id: 1,
    ori_desconocidos: 'N',
    desarrollador: 'N',
    configuracion: 'N'
  })

  const { sendCommand } = useSocket()

  useEffect(() => {
    loadDevices()
    loadCompanies()
  }, [pagination.current_page, search, statusFilter, companyFilter])

  // Escuchar actualizaciones de ubicación en tiempo real
  useEffect(() => {
    const handleLocationUpdate = (event) => {
      const { device_id, location } = event.detail
      console.log('Actualizando ubicación en tiempo real:', device_id, location)
      
      // Actualizar el estado local de dispositivos
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.device_id === device_id 
            ? { ...device, location }
            : device
        )
      )
    }

    // Escuchar actualizaciones de última conexión en tiempo real
    const handleLastSeenUpdate = (event) => {
      const { device_id, last_seen } = event.detail
      console.log('Actualizando última conexión en tiempo real:', device_id, last_seen)
      
      // Actualizar el estado local de dispositivos
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.device_id === device_id 
            ? { ...device, last_seen }
            : device
        )
      )
    }

    // Agregar listeners para los eventos personalizados
    window.addEventListener('deviceLocationUpdated', handleLocationUpdate)
    window.addEventListener('deviceLastSeenUpdated', handleLastSeenUpdate)

    // Cleanup
    return () => {
      window.removeEventListener('deviceLocationUpdated', handleLocationUpdate)
      window.removeEventListener('deviceLastSeenUpdated', handleLastSeenUpdate)
    }
  }, [])

  const loadDevices = async () => {
    try {
      setLoading(true)
      const params = {
        page: pagination.current_page,
        limit: pagination.per_page,
        search,
        status: statusFilter,
        company_id: companyFilter
      }
      const response = await deviceService.getAll(params)
      console.log('Dispositivos cargados:', response.data.devices.map(d => ({ 
        id: d.id, 
        name: d.name, 
        status: d.status, 
        codigo_desbloqueo: d.codigo_desbloqueo 
      })))
      setDevices(response.data.devices)
      setPagination(response.data.pagination)
    } catch (error) {
      toast.error('Error al cargar dispositivos')
    } finally {
      setLoading(false)
    }
  }

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 })
      setCompanies(response.data.companies)
    } catch (error) {
      console.error('Error al cargar empresas:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingDevice) {
        await deviceService.update(editingDevice.id, formData)
        toast.success('Dispositivo actualizado exitosamente')
      } else {
        await deviceService.create(formData)
        toast.success('Dispositivo creado exitosamente')
      }
      setShowModal(false)
      setEditingDevice(null)
      resetForm()
      loadDevices()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar dispositivo')
    }
  }

  const handleEdit = (device) => {
    setEditingDevice(device)
    setFormData({
      device_id: device.device_id,
      name: device.name,
      model: device.model || '',
      brand: device.brand || '',
      os_version: device.os_version || '',
      serial_number: device.serial_number || '',
      imei: device.imei || '',
      status: device.status || 'no_registrado',
      location: device.location || '',
      company_id: device.company_id,
      ori_desconocidos: device.ori_desconocidos || 'N',
      desarrollador: device.desarrollador || 'N',
      configuracion: device.configuracion || 'N'
    })
    setShowModal(true)
  }

  const handleDelete = async (device) => {
    if (!confirm('¿Estás seguro de que quieres desinstalar la aplicación de este dispositivo?')) return
    
    const commandKey = `${device.id}_delete`
    if (loadingCommands[commandKey]) return // Evitar múltiples clics
    
    setLoadingCommands(prev => ({ ...prev, [commandKey]: true }))
    
    try {
      console.log('Enviando comando de desinstalar app a:', device.device_id)
      
      // Obtener el Bearer token desde las variables de entorno
      const bearerToken = import.meta.env.VITE_BEARER_TOKEN_APIS;
      
      // Enviar comando a través del proxy del backend
      const response = await fetch('/api/dispositivos/comando-externo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${bearerToken}`
        },
        body: JSON.stringify({ 
          device_id: device.device_id, 
          comando: 'desinstalar_app' 
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      toast.success('Comando de desinstalar aplicación enviado exitosamente')
      
      // Actualizar el estado en la base de datos
      await deviceService.updateStatus(device.id, 'no_registrado')
      
      // Cambiar el estado del dispositivo a no_registrado
      setDevices(prevDevices => 
        prevDevices.map(d => 
          d.id === device.id 
            ? { ...d, status: 'no_registrado' }
            : d
        )
      )
    } catch (error) {
      console.error('Error al enviar comando de desinstalar app:', error)
      toast.error(`Error al enviar comando de desinstalar app: ${error.message}`)
    } finally {
      setLoadingCommands(prev => ({ ...prev, [commandKey]: false }))
    }
  }

  const handleBlock = async (device) => {
    if (loadingCommands[device.id]) return // Evitar múltiples clics
    
    setLoadingCommands(prev => ({ ...prev, [device.id]: true }))
    
    try {
      console.log('Enviando comando de bloqueo a:', device.device_id)
      
      // Obtener el Bearer token desde las variables de entorno
      const bearerToken = import.meta.env.VITE_BEARER_TOKEN_APIS;
      
      // Enviar comando a través del proxy del backend
      const response = await fetch('/api/dispositivos/comando-externo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${bearerToken}`
        },
        body: JSON.stringify({ 
          device_id: device.device_id, 
          comando: 'bloquear' 
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.json();
      console.log('Respuesta del comando bloquear:', responseData);
      
      toast.success('Dispositivo bloqueado exitosamente')
      
      // Actualizar el estado local del dispositivo con el código de desbloqueo
      setDevices(prevDevices => 
        prevDevices.map(d => 
          d.id === device.id 
            ? { 
                ...d, 
                status: 'bloqueado',
                codigo_desbloqueo: responseData.codigo_desbloqueo
              }
            : d
        )
      )
    } catch (error) {
      console.error('Error al enviar comando de bloqueo:', error)
      toast.error(`Error al bloquear dispositivo: ${error.message}`)
    } finally {
      setLoadingCommands(prev => ({ ...prev, [device.id]: false }))
    }
  }

  const handleUnblock = async (device) => {
    if (loadingCommands[device.id]) return // Evitar múltiples clics
    
    setLoadingCommands(prev => ({ ...prev, [device.id]: true }))
    
    try {
      console.log('Enviando comando de desbloqueo a:', device.device_id)
      
      // Obtener el Bearer token desde las variables de entorno
      const bearerToken = import.meta.env.VITE_BEARER_TOKEN_APIS;
      
      // Enviar comando a través del proxy del backend
      const response = await fetch('/api/dispositivos/comando-externo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${bearerToken}`
        },
        body: JSON.stringify({ 
          device_id: device.device_id, 
          comando: 'desbloquear' 
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // El comando ya actualiza la BD automáticamente, no necesitamos llamar a dispositivoService.unblock()
      // const unblockResponse = await dispositivoService.unblock({ device_id: device.device_id })
      
      toast.success('Comando de desbloqueo enviado exitosamente')
      
      // Actualizar el estado local del dispositivo SIN recargar la lista
      setDevices(prevDevices => 
        prevDevices.map(d => 
          d.id === device.id 
            ? { 
                ...d, 
                status: 'desbloqueado',
                codigo_desbloqueo: d.codigo_desbloqueo // Mantener explícitamente el código
              }
            : d
        )
      )
      
      // Debug: verificar que el código se mantiene
      console.log('Dispositivo desbloqueado, código mantenido:', device.codigo_desbloqueo)
      
      // Debug: verificar el estado después de la actualización
      setTimeout(() => {
        const updatedDevice = devices.find(d => d.id === device.id)
        console.log('Estado después de actualización:', updatedDevice)
      }, 100)
    } catch (error) {
      console.error('Error al enviar comando de desbloqueo:', error)
      toast.error(`Error al desbloquear dispositivo: ${error.message}`)
    } finally {
      setLoadingCommands(prev => ({ ...prev, [device.id]: false }))
    }
  }

  const handleRefreshLocation = async (device) => {
    const commandKey = `${device.id}_refresh_location`
    if (loadingCommands[commandKey]) return // Evitar múltiples clics
    
    setLoadingCommands(prev => ({ ...prev, [commandKey]: true }))
    
    try {
      console.log('Enviando comando de refrescar ubicación a:', device.device_id)
      
      // Obtener el Bearer token desde las variables de entorno
      const bearerToken = import.meta.env.VITE_BEARER_TOKEN_APIS;
      
      // Enviar comando a través del proxy del backend
      const response = await fetch('/api/dispositivos/comando-externo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${bearerToken}`
        },
        body: JSON.stringify({ 
          device_id: device.device_id, 
          comando: 'refrescar_ubi' 
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      toast.success('Comando de refrescar ubicación enviado exitosamente')
      
      // La ubicación se actualizará automáticamente en tiempo real cuando llegue la respuesta
    } catch (error) {
      console.error('Error al enviar comando de refrescar ubicación:', error)
      toast.error(`Error al enviar comando de refrescar ubicación: ${error.message}`)
    } finally {
      setLoadingCommands(prev => ({ ...prev, [commandKey]: false }))
    }
  }

  const handleOriDesconocidos = async (device, checked) => {
    const newValue = checked ? 'S' : 'N'
    const commandKey = `${device.id}_ori_desconocidos`
    
    console.log(`Enviando comando o_desconocidos con enabled: ${checked} para dispositivo ${device.device_id}`)
    
    try {
      // Actualizar en la base de datos inmediatamente
      await deviceService.updateStatus(device.id, device.status, { ori_desconocidos: newValue })
      
      // Obtener el Bearer token desde las variables de entorno
      const bearerToken = import.meta.env.VITE_BEARER_TOKEN_APIS;
      
      // Enviar comando a través del proxy del backend
      const comando = checked ? 'act_o_desconocidos' : 'des_o_desconocidos'
      const response = await fetch('/api/dispositivos/comando-externo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${bearerToken}`
        },
        body: JSON.stringify({ 
          device_id: device.device_id, 
          comando 
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Actualizar estado local
      setDevices(prevDevices => 
        prevDevices.map(d => 
          d.id === device.id 
            ? { ...d, ori_desconocidos: newValue }
            : d
        )
      )
      
      toast.success(`Orígenes desconocidos ${checked ? 'habilitados' : 'deshabilitados'} exitosamente`)
    } catch (error) {
      console.error('Error al cambiar orígenes desconocidos:', error)
      toast.error(`Error al cambiar orígenes desconocidos: ${error.message}`)
    }
  }

  const handleDesarrollador = async (device, checked) => {
    const newValue = checked ? 'S' : 'N'
    const commandKey = `${device.id}_desarrollador`
    
    console.log(`Enviando comando desarrollador con enabled: ${checked} para dispositivo ${device.device_id}`)
    
    try {
      // Actualizar en la base de datos inmediatamente
      await deviceService.updateStatus(device.id, device.status, { desarrollador: newValue })
      
      // Obtener el Bearer token desde las variables de entorno
      const bearerToken = import.meta.env.VITE_BEARER_TOKEN_APIS;
      
      // Enviar comando a través del proxy del backend
      const comando = checked ? 'act_desarrollador' : 'des_desarrollador'
      const response = await fetch('/api/dispositivos/comando-externo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${bearerToken}`
        },
        body: JSON.stringify({ 
          device_id: device.device_id, 
          comando 
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Actualizar estado local
      setDevices(prevDevices => 
        prevDevices.map(d => 
          d.id === device.id 
            ? { ...d, desarrollador: newValue }
            : d
        )
      )
      
      toast.success(`Opciones de desarrollador ${checked ? 'habilitadas' : 'deshabilitadas'} exitosamente`)
    } catch (error) {
      console.error('Error al cambiar opciones de desarrollador:', error)
      toast.error(`Error al cambiar opciones de desarrollador: ${error.message}`)
    }
  }

  const handleConfiguracion = async (device, checked) => {
    const newValue = checked ? 'S' : 'N'
    const commandKey = `${device.id}_configuracion`
    
    console.log(`Enviando comando configuracion con enabled: ${checked} para dispositivo ${device.device_id}`)
    
    try {
      // Actualizar en la base de datos inmediatamente
      await deviceService.updateStatus(device.id, device.status, { configuracion: newValue })
      
      // Obtener el Bearer token desde las variables de entorno
      const bearerToken = import.meta.env.VITE_BEARER_TOKEN_APIS;
      
      // Enviar comando a través del proxy del backend
      const comando = checked ? 'act_configuracion' : 'des_configuracion'
      const response = await fetch('/api/dispositivos/comando-externo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${bearerToken}`
        },
        body: JSON.stringify({ 
          device_id: device.device_id, 
          comando 
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Actualizar estado local
      setDevices(prevDevices => 
        prevDevices.map(d => 
          d.id === device.id 
            ? { ...d, configuracion: newValue }
            : d
        )
      )
      
      toast.success(`Configuración ${checked ? 'habilitada' : 'deshabilitada'} exitosamente`)
    } catch (error) {
      console.error('Error al cambiar configuración:', error)
      toast.error(`Error al cambiar configuración: ${error.message}`)
    }
  }

  const handleLastConnection = async (device) => {
    if (loadingCommands[`${device.id}_last_connection`]) return // Evitar múltiples clics
    
    setLoadingCommands(prev => ({ ...prev, [`${device.id}_last_connection`]: true }))
    
    try {
      // last_seen se actualiza solo con la actividad del TV (WS/FCM).
      // Este botón solo refresca el valor guardado en BD.
      const response = await deviceService.getById(device.id)
      const lastSeen = response.data?.device?.last_seen

      if (!lastSeen) {
        toast('Sin registro de última conexión aún', { icon: 'ℹ️' })
        return
      }

      setDevices(prevDevices => 
        prevDevices.map(d => 
          d.id === device.id 
            ? { ...d, last_seen: lastSeen }
            : d
        )
      )

      toast.success(`Última conexión: ${new Date(lastSeen).toLocaleString('es-ES')}`)
    } catch (error) {
      console.error('Error al consultar última conexión:', error)
      toast.error(`Error al consultar última conexión: ${error.message}`)
    } finally {
      setLoadingCommands(prev => ({ ...prev, [`${device.id}_last_connection`]: false }))
    }
  }

  const handleRevivirConexion = async (device) => {
    if (loadingCommands[`${device.id}_revivir_conexion`]) return // Evitar múltiples clics
    
    setLoadingCommands(prev => ({ ...prev, [`${device.id}_revivir_conexion`]: true }))
    
    try {
      console.log('Reviviendo conexión del dispositivo:', device.device_id)
      
      // Llamar al servicio de revivir conexión
      const response = await deviceService.revivirConexion(device.id)
      
      toast.success('Mensaje de revivir conexión enviado exitosamente')
      console.log('Respuesta de revivir conexión:', response)
      
    } catch (error) {
      console.error('Error al revivir conexión:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Error al revivir conexión'
      
      // Mostrar mensaje más informativo
      if (error.response?.status === 400 && error.response?.data?.error === 'Token FCM no disponible') {
        toast.error('Este dispositivo no tiene token FCM. Debe estar conectado previamente.')
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setLoadingCommands(prev => ({ ...prev, [`${device.id}_revivir_conexion`]: false }))
    }
  }

  const resetForm = () => {
    setFormData({
      device_id: '',
      name: '',
      model: '',
      brand: '',
      os_version: '',
      serial_number: '',
      imei: '',
      status: 'no_registrado',
      location: '',
      company_id: 1,
      ori_desconocidos: 'N',
      desarrollador: 'N',
      configuracion: 'N'
    })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      desbloqueado: { color: 'bg-green-100 text-green-800', text: 'Desbloqueado' },
      bloqueado: { color: 'bg-red-100 text-red-800', text: 'Bloqueado' },
      inactivo: { color: 'bg-gray-100 text-gray-800', text: 'Inactivo' },
      no_registrado: { color: 'bg-yellow-100 text-yellow-800', text: 'No Registrado' }
    }
    const config = statusConfig[status] || statusConfig.inactivo
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    )
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dispositivos</h1>
          <p className="text-gray-600">Gestiona los dispositivos TV del sistema</p>
        </div>
        <button
          onClick={() => {
            setEditingDevice(null)
            resetForm()
            setShowModal(true)
          }}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Dispositivo
        </button>
      </div>

      {/* Filtros */}
      <div className="card mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar dispositivos..."
                  className="input pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input"
              >
                <option value="">Todos</option>
                <option value="desbloqueado">Desbloqueado</option>
                <option value="bloqueado">Bloqueado</option>
                <option value="inactivo">Inactivo</option>
                <option value="no_registrado">No Registrado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Empresa
              </label>
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="input"
              >
                <option value="">Todas</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearch('')
                  setStatusFilter('')
                  setCompanyFilter('')
                  setPagination(prev => ({ ...prev, current_page: 1 }))
                }}
                className="btn btn-outline w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Dispositivo</th>
                <th>Empresa</th>
                <th>Estado</th>
                <th>Código Extraído</th>
                <th>Ubicación</th>
                <th>Último Visto</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  </td>
                </tr>
              ) : devices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    No se encontraron dispositivos
                  </td>
                </tr>
              ) : (
                devices.map(device => (
                  <tr key={device.id} className="hover:bg-gray-50">
                    <td>
                      <div>
                        <div className="font-medium text-gray-900">{device.name}</div>
                        <div className="text-sm text-gray-500">{device.device_id}</div>
                        {device.model && (
                          <div className="text-xs text-gray-400">{device.brand} {device.model}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm text-gray-900">
                        {device.Company?.name || 'Sin empresa'}
                      </div>
                    </td>
                                        <td>{getStatusBadge(device.status)}</td>
                    <td>
                      {device.codigo_generado ? (
                        <div className="relative group">
                          <div className="text-sm font-mono font-bold px-2 py-1 rounded border text-green-600 bg-green-50 cursor-help">
                            {device.codigo_generado}
                          </div>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                            Código extraído del sistema externo
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">-</div>
                      )}
                    </td>
                    <td>
                      <div className="text-sm text-gray-900">{device.location || '-'}</div>
                    </td>
                    <td>
                      <div className="text-sm text-gray-900">
                        {device.last_seen ? new Date(device.last_seen).toLocaleString('es-ES', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        }) : 'Nunca'}
                      </div>
                    </td>
                    <td>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(device)}
                          disabled={device.status === 'no_registrado'}
                          className={`text-blue-600 hover:text-blue-900 ${device.status === 'no_registrado' ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={device.status === 'no_registrado' ? 'Dispositivo no registrado' : 'Editar'}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleRefreshLocation(device)}
                          disabled={loadingCommands[`${device.id}_refresh_location`] || device.status === 'no_registrado'}
                          className={`text-purple-600 hover:text-purple-900 ${(loadingCommands[`${device.id}_refresh_location`] || device.status === 'no_registrado') ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={device.status === 'no_registrado' ? 'Dispositivo no registrado' : 'Refrescar Ubicación'}
                        >
                          {loadingCommands[`${device.id}_refresh_location`] ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                          ) : (
                            <MapPin className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleLastConnection(device)}
                          disabled={loadingCommands[`${device.id}_last_connection`] || device.status === 'no_registrado'}
                          className={`text-orange-600 hover:text-orange-900 ${(loadingCommands[`${device.id}_last_connection`] || device.status === 'no_registrado') ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={device.status === 'no_registrado' ? 'Dispositivo no registrado' : 'Ver última conexión registrada'}
                        >
                          {loadingCommands[`${device.id}_last_connection`] ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                          ) : (
                            <Clock className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleRevivirConexion(device)}
                          disabled={loadingCommands[`${device.id}_revivir_conexion`] || device.status === 'no_registrado' || !device.fcm_token}
                          className={`${!device.fcm_token ? 'text-gray-400 cursor-not-allowed' : 'text-cyan-600 hover:text-cyan-900'} ${(loadingCommands[`${device.id}_revivir_conexion`] || device.status === 'no_registrado') ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={!device.fcm_token ? 'Sin token FCM - No disponible' : (device.status === 'no_registrado' ? 'Dispositivo no registrado' : 'Revivir Conexión')}
                        >
                          {loadingCommands[`${device.id}_revivir_conexion`] ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-600"></div>
                          ) : (
                            <Wifi className="h-4 w-4" />
                          )}
                        </button>
                        {device.status === 'bloqueado' ? (
                          <button
                            onClick={() => handleUnblock(device)}
                            disabled={loadingCommands[device.id] || device.status === 'no_registrado'}
                            className={`text-green-600 hover:text-green-900 ${(loadingCommands[device.id] || device.status === 'no_registrado') ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={device.status === 'no_registrado' ? 'Dispositivo no registrado' : 'Desbloquear'}
                          >
                            {loadingCommands[device.id] ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                            ) : (
                              <ShieldOff className="h-4 w-4" />
                            )}
                          </button>
                        ) : device.status !== 'no_registrado' ? (
                          <button
                            onClick={() => handleBlock(device)}
                            disabled={loadingCommands[device.id]}
                            className={`text-red-600 hover:text-red-900 ${loadingCommands[device.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Bloquear"
                          >
                            {loadingCommands[device.id] ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <Shield className="h-4 w-4" />
                            )}
                          </button>
                        ) : null}
                        <button
                          onClick={() => handleDelete(device)}
                          disabled={loadingCommands[`${device.id}_delete`] || device.status === 'no_registrado'}
                          className={`text-red-600 hover:text-red-900 ${(loadingCommands[`${device.id}_delete`] || device.status === 'no_registrado') ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={device.status === 'no_registrado' ? 'Dispositivo no registrado' : 'Desinstalar aplicación'}
                        >
                          {loadingCommands[`${device.id}_delete`] ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {pagination.total_pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de{' '}
                {pagination.total} resultados
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                  disabled={pagination.current_page === 1}
                  className="btn btn-outline btn-sm disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Página {pagination.current_page} de {pagination.total_pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                  disabled={pagination.current_page === pagination.total_pages}
                  className="btn btn-outline btn-sm disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingDevice ? 'Editar Dispositivo' : 'Nuevo Dispositivo'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">ID del Dispositivo (7 dígitos)</label>
                  <input
                    type="text"
                    value={formData.device_id}
                    onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
                    className="input"
                    placeholder="1234567"
                    maxLength="7"
                    pattern="[0-9]{7}"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nombre</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Empresa</label>
                  <select
                    value={formData.company_id}
                    onChange={(e) => setFormData({ ...formData, company_id: parseInt(e.target.value) })}
                    className="input"
                    required
                  >
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Marca</label>
                    <input
                      type="text"
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Modelo</label>
                    <input
                      type="text"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Versión del Sistema Operativo</label>
                  <input
                    type="text"
                    value={formData.os_version}
                    onChange={(e) => setFormData({ ...formData, os_version: e.target.value })}
                    className="input"
                    placeholder="Android 11"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Número de Serie</label>
                    <input
                      type="text"
                      value={formData.serial_number}
                      onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">IMEI</label>
                    <input
                      type="text"
                      value={formData.imei}
                      onChange={(e) => setFormData({ ...formData, imei: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input"
                  >
                    <option value="desbloqueado">Desbloqueado</option>
                    <option value="bloqueado">Bloqueado</option>
                    <option value="inactivo">Inactivo</option>
                    <option value="no_registrado">No Registrado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ubicación</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="input"
                    placeholder="Sala principal, Piso 1"
                  />
                </div>
                
                {/* Checkboxes para configuraciones */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Configuraciones del Dispositivo</h4>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="ori_desconocidos"
                      checked={formData.ori_desconocidos === 'S'}
                      onChange={(e) => {
                        const newValue = e.target.checked ? 'S' : 'N'
                        setFormData({ ...formData, ori_desconocidos: newValue })
                        if (editingDevice) {
                          handleOriDesconocidos(editingDevice, e.target.checked)
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="ori_desconocidos" className="ml-2 block text-sm text-gray-900">
                      Orígenes Desconocidos
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="desarrollador"
                      checked={formData.desarrollador === 'S'}
                      onChange={(e) => {
                        const newValue = e.target.checked ? 'S' : 'N'
                        setFormData({ ...formData, desarrollador: newValue })
                        if (editingDevice) {
                          handleDesarrollador(editingDevice, e.target.checked)
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="desarrollador" className="ml-2 block text-sm text-gray-900">
                      Modo Desarrollador
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="configuracion"
                      checked={formData.configuracion === 'S'}
                      onChange={(e) => {
                        const newValue = e.target.checked ? 'S' : 'N'
                        setFormData({ ...formData, configuracion: newValue })
                        if (editingDevice) {
                          handleConfiguracion(editingDevice, e.target.checked)
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="configuracion" className="ml-2 block text-sm text-gray-900">
                      Configuración
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-outline"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingDevice ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Devices 